import { useTranslation } from "react-i18next";
import { ArrowUpRight, BellDot, BookOpenText, ListChecks } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useId } from "react";

import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";

import { INITIAL_GREETING_KEY } from "../constants";
import { useChat } from "../context/use-chat";
import { useMayaSidebar } from "../context/use-maya-sidebar";
import { useMayaSpace } from "../context/use-maya-space";

import { MayaOrb } from "./maya-orb";

interface QuickAction {
  icon: LucideIcon;
  /** i18n key of the card label. */
  label: string;
  /** i18n key of the message it sends — fuller than the label. Translated,
   *  like the advisor starters below it: it shows in the chat as the user's
   *  own message, so it reads in their language (I18N.md). */
  prompt: string;
}

// Page-agnostic on purpose: the page itself travels as context, so "this
// page" always means the one open beside the sidebar.
const QUICK_ACTIONS: QuickAction[] = [
  {
    icon: ListChecks,
    label: "common:maya.summarize_this_page",
    prompt: "common:maya.summarize_this_page_prompt",
  },
  {
    icon: BellDot,
    label: "common:maya.what_needs_my_attention",
    prompt: "common:maya.what_needs_my_attention_prompt",
  },
  {
    icon: BookOpenText,
    label: "common:maya.explain_this_page",
    prompt: "common:maya.explain_this_page_prompt",
  },
];

/** One tappable suggestion: a card-sized row (asChild, because Button wraps
 *  plain children in a centred span that would bunch the row up). */
const SuggestionCard = ({
  icon: Icon,
  title,
  description,
  delay,
  onSelect,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  delay: number;
  onSelect: () => void;
}) => (
  <li className="animate-rise-in" style={{ animationDelay: `${delay}ms` }}>
    <Button
      asChild
      variant="outline"
      className="group/qa surface-frosted maya-qa h-auto w-full justify-start gap-3 rounded-md px-3 py-2.5 text-start transition-all hover:-translate-y-px hover:border-primary/40 hover:bg-surface"
    >
      <button type="button" onClick={onSelect}>
        <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-primary-muted text-primary transition-colors group-hover/qa:bg-primary group-hover/qa:text-primary-foreground">
          <Icon className="size-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-xs font-medium text-foreground">
            {title}
          </span>
          {description && (
            <span className="truncate text-[11px] font-normal text-muted-foreground">
              {description}
            </span>
          )}
        </span>
        <ArrowUpRight className="rtl-flip size-3.5 shrink-0 text-muted-foreground transition-transform group-hover/qa:-translate-y-0.5 group-hover/qa:text-primary" />
      </button>
    </Button>
  </li>
);

export const EmptyState = () => {
  const { t } = useTranslation(["common", "ai-assistant"]);
  const { pageContext, sendMessage, advisor, temporary } = useChat();
  const { sharePageContext } = useMayaSidebar();
  const space = useMayaSpace();
  const { user } = useAppSelector(selectUser);
  // The advisor's starter ideas, as on the space's full page.
  const starters = space.starters(advisor.slug, t);
  const pageActionsCount = sharePageContext ? QUICK_ACTIONS.length : 0;
  const suggestionsId = useId();

  return (
    <div className="flex flex-col items-center gap-4 px-2 pt-4 pb-4 text-center">
      {/* Room for the orbiting sparkles, which reach past the orb. */}
      <div className="animate-rise-in py-4">
        <MayaOrb size={76} particles />
      </div>
      <div
        className="animate-rise-in flex flex-col gap-1"
        style={{ animationDelay: "60ms" }}
      >
        <h2 className="text-base font-bold text-foreground">
          {user?.firstName
            ? t("common:maya.hi_name", { name: user.firstName })
            : t(INITIAL_GREETING_KEY)}
        </h2>
        <p className="mx-auto max-w-70 text-xs leading-snug text-muted-foreground">
          {sharePageContext
            ? t("common:maya.need_a_hand_with_page", {
                page: pageContext.label,
              })
            : t("common:maya.lens_off_hint")}
        </p>
        {temporary && (
          <p className="mx-auto mt-1 max-w-70 rounded-xs bg-warning-muted px-2 py-1 text-[11px] leading-snug text-foreground">
            {t("common:ai_assistant.temporary_chat_not_saved")}
          </p>
        )}
      </div>

      {sharePageContext && (
        <ul className="flex w-full flex-col gap-2">
          {QUICK_ACTIONS.map(({ icon, label, prompt }, i) => (
            <SuggestionCard
              key={label}
              icon={icon}
              title={t(label)}
              delay={120 + i * 60}
              onSelect={() => void sendMessage(t(prompt))}
            />
          ))}
        </ul>
      )}

      {starters.length > 0 && (
        <div className="flex w-full flex-col gap-2 text-start">
          <p
            id={suggestionsId}
            className="px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {t("common:maya.suggestions")}
          </p>
          <ul
            aria-labelledby={suggestionsId}
            className="flex w-full flex-col gap-2"
          >
            {starters.map((starter, i) => (
              <SuggestionCard
                key={starter.title}
                icon={starter.icon}
                title={starter.title}
                description={starter.description}
                delay={120 + (pageActionsCount + i) * 60}
                onSelect={() => void sendMessage(starter.prompt)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
